import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, streamJob } from "../api/client";
import { Icon } from "./icons";
import { toast } from "./toast";

/**
 * Re-proof with the cleaned script.
 *
 * Mockingbird now sets a script's notes, comments and crossed-out words aside
 * instead of reading them as the book's words. Files proofed before that still
 * carry the old reading - every word of a note on their pages is a "missing"
 * flag - until they are proofed again. The server knows which files those are
 * (script-check); this offers to re-proof them, once, as one background job.
 * It reuses the transcripts already made and keeps the review work.
 *
 * Shown in the report (above the flags) and on the Project Hub (in the Audio
 * Files card). Never silent while it works: the first look at an older book
 * reads the whole script in the background, so it says it is checking; a re-proof
 * under way says so; a read that failed says so. A clean or current book shows
 * nothing.
 */

export interface ScriptCheck {
  state: "none" | "running" | "checking" | "failed" | "clean" | "current" | "needed";
  /** "notes": proofed while notes still counted as text; "changed": proofed
   *  before the manuscript summary's "keep as text" switches were changed. */
  why?: "notes" | "changed";
  files?: { chapter_id: string; audio_filename: string | null; pages: number; words: Record<string, number> }[];
  pages?: number;
  words?: Record<string, number>;
}

/** The project's script check, shared by the report, the Hub and its file rows. */
export function useScriptCheck(projectId: string) {
  return useQuery({
    queryKey: ["script-check", projectId],
    queryFn: () => api<ScriptCheck>(`/api/modules/proofing/projects/${projectId}/script-check`),
    // Ask again while the answer is on its way: the script being read, or the
    // re-proof running (a page reloaded mid-job has no other way to hear it end).
    refetchInterval: (q) => (["checking", "running"].includes(q.state.data?.state ?? "") ? 8000 : false),
    staleTime: 60_000,
    enabled: !!projectId,
  });
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

export function ScriptReproofBanner({ projectId, busy = false, inCard = false }: {
  projectId: string;
  /** Something else is already saying a re-proof is running (the report's own hint). */
  busy?: boolean;
  /** Inside the Hub's Audio Files card rather than above the report's flags. */
  inCard?: boolean;
}) {
  const qc = useQueryClient();
  const [later, setLater] = useState(false);
  const [starting, setStarting] = useState(false);
  const stopWatching = useRef<(() => void) | null>(null);
  useEffect(() => () => stopWatching.current?.(), []);
  const { data } = useScriptCheck(projectId);

  // A re-proof that ended while nobody was watching its job (the page was
  // reloaded): refresh what it changed.
  const was = useRef(data?.state);
  useEffect(() => {
    if (was.current === "running" && data?.state && data.state !== "running") {
      qc.invalidateQueries({ queryKey: ["result", projectId] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      qc.invalidateQueries({ queryKey: ["script-notes", projectId] });
      qc.invalidateQueries({ queryKey: ["script-snippet", projectId] });
    }
    was.current = data?.state;
  }, [data?.state, projectId, qc]);

  if (!data || busy) return null;
  const where = inCard ? " in-card" : "";

  if (data.state === "checking")
    return (
      <div className={`script-check-line${where}`} role="status">
        <span className="reproof-dot" /> Checking this script for notes, comments and crossed-out words…
      </div>
    );
  if (data.state === "running")
    return (
      <div className={`script-check-line${where}`} role="status">
        <span className="reproof-dot" /> Re-proofing with the cleaned script — the flags refresh when it's done.
      </div>
    );
  if (data.state === "failed")
    return (
      <div className={`script-check-line quiet${where}`} role="status">
        <Icon name="alert" size={14} /> Couldn't check this script for notes just now — Mockingbird tries again in a few
        minutes.
      </div>
    );
  if (later || data.state !== "needed" || !data.files?.length) return null;

  const files = data.files.length;
  const words = Object.values(data.words ?? {}).reduce((a, b) => a + b, 0);

  async function start() {
    setStarting(true);
    try {
      const r = await api<{ job_id: string | null; files: number }>(
        `/api/modules/proofing/projects/${projectId}/script-check/reproof`, { method: "POST" });
      if (r.job_id) {
        toast(`Re-proofing ${plural(r.files, "file")} with the cleaned script — the flags refresh when it's done.`, "info");
        stopWatching.current?.();
        stopWatching.current = streamJob(r.job_id, {
          onEnd: (m) => {
            const ok = (m?.status ?? "completed") === "completed";
            toast(ok ? "Re-proofed with the cleaned script." : "The re-proof stopped — History has the details.",
              ok ? "success" : "error");
            qc.invalidateQueries({ queryKey: ["result", projectId] });
            qc.invalidateQueries({ queryKey: ["project", projectId] });
            qc.invalidateQueries({ queryKey: ["script-check", projectId] });
            qc.invalidateQueries({ queryKey: ["script-notes", projectId] });
            qc.invalidateQueries({ queryKey: ["script-snippet", projectId] });
          },
        });
      }
      qc.invalidateQueries({ queryKey: ["project", projectId] });     // picks up the running job
      qc.invalidateQueries({ queryKey: ["script-check", projectId] });
    } catch (e: any) {
      toast(e?.message || "Couldn't start the re-proof — please try again.", "error");
    } finally {
      setStarting(false);
    }
  }

  return (
    <div className={`script-reproof${where}`} role="status">
      <Icon name="alert" size={16} className="script-reproof-icon" />
      <div className="script-reproof-text">
        {data.why === "changed" ? (
          <>
            <b>You changed which of the script's marks count as text.</b>{" "}
            {plural(files, "file was", "files were")} proofed before that, on {plural(data.pages ?? 0, "page")} where
            it makes a difference. Re-proof {files === 1 ? "it" : "them"} to apply the change — this uses the
            transcripts you already have and keeps your pickups and review work.
          </>
        ) : (
          <>
            <b>Some flags are just the script's notes.</b>{" "}
            {plural(files, "file was", "files were")} proofed when {plural(words, "word")} of notes, comments or
            crossed-out words on {plural(data.pages ?? 0, "page")} still counted as the book's text. Mockingbird now sets
            them aside. Re-proofing uses the transcripts you already have and keeps your pickups and review work.
          </>
        )}
      </div>
      <div className="script-reproof-actions">
        <button className="btn primary sm" onClick={start} disabled={starting}>
          {starting ? "Starting…" : `Re-proof ${plural(files, "file")}`}
        </button>
        <button className="btn ghost sm" onClick={() => setLater(true)} disabled={starting}>Not now</button>
      </div>
    </div>
  );
}
